import { SwapOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Input, Modal, Select, Table } from 'antd';
import React, { useState } from 'react';
import { fetchAPI } from '../../utils/api';
import { FIVE_MINUTES } from '../../constants/CONSTANTS';
import { formatUTCTimeToLocal } from '../../utils/format';
import ReactJson from 'react-json-view';
import EditableText from '../../components/EditableText';
import styled from 'styled-components';

export type ExternalOrder = {
  updated_at: string; // "2025-07-30 03:11:06",
  created_at: string; // "2025-07-30 03:11:06",
  is_soft_deleted: boolean; // false,
  id: number; // 3,
  source_email: string; // "Lauretta.Legros@example.org",
  source_subject: string; // "PO Email about: string; // Senior Usability Associate",
  invoice_no: string; // "2705d46f-c7a8-4555-8016-7f3445e3346f",
  invoice_total: number; // 1230.0,
  source_type: 'EMAIL_READER' | 'OTHER'; // "EMAIL_READER",
  order_id?: number; // null,
  po_payload: any; // { "program": string; // "dolore-consequatur-cum" }
};

const OrderModalMapExternalOrder = () => {
  const [visible, setVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState({
    page: 1,
    q: '',
  });
  const [recordType, setRecordType] = useState('WORK_ORDER');
  const [mappedState, setMappedState] = useState(0);

  const externalOrders = useQuery<{
    items: ExternalOrder[];
    page: number;
    per_page: number;
    total: number;
    loading: boolean;
  }>(
    ['external-orders-on-modal', filters, visible, recordType, mappedState],
    async () => {
      if (!visible) {
        return {
          items: [],
          page: filters.page,
          per_page: 5,
          total: 0,
          loading: false,
        };
      }
      try {
        const resp = await fetchAPI('order/external/list', {
          method: 'POST',
          payload: {
            ...filters,
            per_page: 5,
            filters: {
              record_type: recordType,
              mapped_state: mappedState,
            },
          },
        });

        return resp;
      } catch (error) {
        return {
          loading: false,
          items: [],
          page: filters.page,
          per_page: 5,
          total: 0,
        };
      }
    },
    {
      staleTime: FIVE_MINUTES,
      placeholderData: {
        items: [],
        loading: true,
        page: filters.page,
        per_page: 5,
        total: 0,
      },
    }
  );

  const saveOrderId = async (externalOrderId: number, val: string) => {
    setSaving(true);
    await fetchAPI(`order/external/update/${externalOrderId}`, {
      method: 'PATCH',
      payload: {
        order_id: Number(val),
      },
    });
    setSaving(false);
    // try {
    // } catch (error) {
    //   message.error(
    //     'Failed save order ID for external Order ' + externalOrderId
    //   );
    // }
  };

  return (
    <>
      <Button
        icon={<SwapOutlined />}
        onClick={() => {
          setVisible(true);
          setRecordType('WORK_ORDER');
          setMappedState(0);
        }}
      >
        Map External Order
      </Button>
      <Modal
        title="List of External Order"
        visible={visible}
        onCancel={() => {
          if (!saving) {
            setVisible(false);
          }
        }}
        width={1000}
        okButtonProps={{ disabled: saving }}
        cancelButtonProps={{ disabled: saving }}
        // onOk={() => saveSelectedExternalOrder()}
      >
        <FilterWrapper>
          <div className="filter-col">
            <p>Search External Order</p>
            <Input.Search
              enterButton
              placeholder="Search External Order"
              allowClear
              onSearch={value => {
                setFilters({ q: value || '', page: 1 });
              }}
            />
          </div>
          <div className="filter-col">
            <p>Record Type</p>
            <Select
              value={recordType}
              onChange={val => setRecordType(val)}
              allowClear
            >
              {['REMITTANCE', 'WORK_ORDER'].map(str => (
                <Select.Option value={str}>
                  {str.replaceAll('_', ' ')}
                </Select.Option>
              ))}
            </Select>
          </div>
          <div className="filter-col">
            <p>Map Status</p>
            <Select
              value={mappedState || undefined}
              onChange={val => setMappedState(val)}
              placeholder="Map/Not-Mapped External Order"
              allowClear
            >
              <Select value={1}>Mapped</Select>
              <Select value={2}>Not Mapped</Select>
            </Select>
          </div>
        </FilterWrapper>
        <Table
          dataSource={externalOrders.data.items}
          loading={externalOrders.data.loading || externalOrders.isLoading}
          pagination={{
            size: 'small',
            showSizeChanger: false,
            current: filters.page,
            pageSize: 5,
            total: externalOrders.data.total,
            onChange(page) {
              setFilters(fils => ({ ...fils, page }));
            },
            style: { marginBottom: -8 },
          }}
          style={{ marginTop: 15 }}
          scroll={{
            x: 1750,
          }}
          columns={[
            { title: 'ID', key: 'id', dataIndex: 'id', fixed: true, width: 75 },
            {
              title: 'SAID',
              key: 'order-id',
              width: 132,
              fixed: true,
              render: (t, r) => {
                return (
                  <div>
                    <EditableText
                      text={String(r.order_id || '')}
                      onSave={val => saveOrderId(r.id, val)}
                    />
                  </div>
                );
              },
            },
            {
              title: 'Source Email',
              key: 'source-email',
              dataIndex: 'source_email',
              width: 250,
            },
            {
              title: 'Source Subject',
              key: 'source-subject',
              dataIndex: 'source_subject',
              width: 250,
            },
            {
              title: 'Entity Name',
              key: 'entity-name',
              dataIndex: 'entity_name',
              width: 250,
            },
            {
              title: 'Invoice No.',
              key: 'invoice-no',
              dataIndex: 'invoice_no',
              width: 250,
            },
            {
              title: 'Invoice Total',
              key: 'invoice_total',
              dataIndex: 'invoice_total',
              width: 250,
            },
            {
              title: 'Source Type',
              key: 'source-type',
              dataIndex: 'source_type',
              width: 250,
            },
            {
              title: 'Created at',
              key: 'created-at',
              render: (t, r) => {
                if (!r.created_at) {
                  return '-';
                }

                return formatUTCTimeToLocal(r.created_at, 'HH:mm, DD MMM YYYY');
              },
              width: 250,
            },
            {
              title: 'PO Payload',
              key: 'po-payload',
              render: (t, r) => {
                return (
                  <ReactJson
                    name="po_payload"
                    style={{ marginTop: 10 }}
                    src={r.po_payload || {}}
                    displayDataTypes={false}
                    indentWidth={2}
                    collapsed
                  />
                );
              },
              width: 250,
            },
          ]}
        />
      </Modal>
    </>
  );
};

const FilterWrapper = styled.div`
  display: flex;
  gap: 15px;

  .filter-col {
    flex: 1;

    p {
      margin: 0;
    }

    .ant-select {
      width: 100%;
    }
  }
`;

export default OrderModalMapExternalOrder;
